/**
 * Motion model for the complexity progress bar's fill (MUS-461).
 *
 * MUS-401 shipped the ease-out half of the animation: every change to the fill
 * eased toward its new value over a fixed 800ms, in both directions and
 * regardless of what came before it. This is the other half.
 *
 * The fill now *lags* the true value at a constant rate rather than easing over
 * a fixed duration, so a small step lands quickly and a large one takes longer,
 * the way a physical needle would. On a sustained climb it picks up momentum
 * and runs faster; that speed-up is gated on Streak Boost and is forward-only,
 * so a drop always falls at the base rate however fast the bar was climbing
 * when it turned. Near the target the rate eases off, so the bar settles into
 * its value instead of stopping dead.
 *
 * The bar always lands exactly on the true value — the motion only smooths the
 * journey, it never changes the destination.
 *
 * The model is pure and time-stepped so it can be tested without a DOM or a
 * frame loop; {@link useMomentumFill} is the thin driver that runs it against
 * `requestAnimationFrame`.
 */

/**
 * Base travel rate, in fill fractions per second.
 *
 * Sized against a real step rather than chosen for feel alone: at the shipping
 * Climb Rate the band moves about 0.125 of the bar on a good phrase, which lands
 * in a little under a second here — close to the 800ms ease it replaces, so the
 * bar reads as it did before on an isolated step and only diverges once a climb
 * sustains.
 */
export const FILL_BASE_RATE_PER_SEC = 0.26;

/** Fastest a sustained climb may run, as a multiple of the base rate. */
export const FILL_MOMENTUM_MAX_MULTIPLIER = 2.4;

/**
 * Consecutive climbs at which momentum is fully wound up.
 *
 * The first climb of a run is not a sustained climb, so momentum starts
 * building on the second and reaches its ceiling on the fourth.
 */
export const FILL_MOMENTUM_FULL_RUN = 4;

/**
 * How close to the target the ease-out begins, in fill fractions.
 *
 * Inside this zone the rate falls away with the distance remaining, which is
 * what turns an arrival into a settle.
 */
export const FILL_EASE_ZONE = 0.06;

/**
 * Floor under the ease-out, as a fraction of the current rate.
 *
 * Without it the approach is asymptotic and the last sliver of travel never
 * completes; with it the bar still slows visibly but always arrives.
 */
export const FILL_EASE_FLOOR = 0.2;

/** Distance at which the remaining travel is not worth a frame. */
export const FILL_SNAP_EPSILON = 0.0005;

/**
 * How long the fill may sit on its target before a climb counts as over.
 *
 * Momentum is meant to reward a climb that keeps going, so a run that stopped
 * a while ago must not lend its speed to the next isolated step.
 */
export const FILL_PLATEAU_RESET_MS = 2500;

/**
 * Longest frame the model will integrate in one step.
 *
 * A backgrounded tab resumes with an enormous delta; without this the bar would
 * teleport on the first frame back rather than resuming its travel.
 */
export const FILL_MAX_FRAME_MS = 64;

/** The fill's position and the climb behind it. */
export interface FillMotion {
  /** What is drawn now, in [0, 1]. */
  readonly value: number;
  /** What it is travelling toward, in [0, 1]. */
  readonly target: number;
  /** Consecutive upward retargets, including the one in flight. */
  readonly climbRun: number;
}

const clampFraction = (fraction: number): number => Math.min(Math.max(fraction, 0), 1);

/**
 * A motion parked on `initialValue`.
 *
 * It starts *at* the value rather than travelling toward it, so the first fill
 * a lesson reports is seeded rather than animated in from zero.
 */
export const createFillMotion = (initialValue: number): FillMotion => {
  const value = clampFraction(initialValue);
  return { value, target: value, climbRun: 0 };
};

/**
 * How much faster than the base rate a climb of `climbRun` steps runs.
 *
 * Returns 1 — no speed-up at all — when Streak Boost is off, and on the first
 * climb of a run whether it is on or not.
 */
export const momentumMultiplier = (climbRun: number, streakBoost: boolean): number => {
  if (!streakBoost || climbRun <= 1) return 1;
  const wound = Math.min((climbRun - 1) / (FILL_MOMENTUM_FULL_RUN - 1), 1);
  return 1 + (FILL_MOMENTUM_MAX_MULTIPLIER - 1) * wound;
};

/**
 * Points the motion at a new target.
 *
 * `idleMs` is how long the fill has been parked on its old target; a run that
 * has been still for {@link FILL_PLATEAU_RESET_MS} has plateaued and starts
 * over. A downward retarget ends the run outright.
 */
export const retargetFill = (motion: FillMotion, nextTarget: number, idleMs = 0): FillMotion => {
  const target = clampFraction(nextTarget);
  if (target === motion.target) return motion;

  const plateaued = idleMs >= FILL_PLATEAU_RESET_MS;
  const runSoFar = plateaued ? 0 : motion.climbRun;
  const climbRun = target > motion.target ? runSoFar + 1 : 0;

  return { ...motion, target, climbRun };
};

/**
 * Travel rate for the frame, in fill fractions per second.
 *
 * Exported for the tests and the visual fixture; the sign of the journey is the
 * caller's business, this is speed alone.
 */
export const fillRatePerSecond = (motion: FillMotion, streakBoost: boolean): number => {
  const remaining = motion.target - motion.value;
  // Forward-only: a drop never inherits the climb's speed, however fast the bar
  // was running when it turned.
  const climbing = remaining > 0;
  const multiplier = climbing ? momentumMultiplier(motion.climbRun, streakBoost) : 1;
  const easing = Math.max(Math.min(Math.abs(remaining) / FILL_EASE_ZONE, 1), FILL_EASE_FLOOR);
  return FILL_BASE_RATE_PER_SEC * multiplier * easing;
};

/** Has the fill arrived, to within a frame's worth of noticing? */
export const fillHasSettled = (motion: FillMotion): boolean =>
  Math.abs(motion.target - motion.value) <= FILL_SNAP_EPSILON;

/**
 * Advances the fill by one frame of `elapsedMs`.
 *
 * Never overshoots: a step long enough to cross the target lands on it exactly,
 * which is what makes the bar's resting place the true value rather than
 * whatever the animation happened to reach.
 */
export const advanceFill = (
  motion: FillMotion,
  elapsedMs: number,
  streakBoost: boolean,
): FillMotion => {
  const remaining = motion.target - motion.value;
  if (Math.abs(remaining) <= FILL_SNAP_EPSILON) {
    return motion.value === motion.target ? motion : { ...motion, value: motion.target };
  }

  const frameMs = Math.min(Math.max(elapsedMs, 0), FILL_MAX_FRAME_MS);
  const step = fillRatePerSecond(motion, streakBoost) * (frameMs / 1000);
  // Lands on the frame that gets within a hair of the target rather than the
  // one after it, so a settled motion is *on* the true value and not merely
  // near it — no caller has to snap it for the arithmetic to come out.
  if (step + FILL_SNAP_EPSILON >= Math.abs(remaining)) return { ...motion, value: motion.target };

  return { ...motion, value: motion.value + Math.sign(remaining) * step };
};
